import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { ScrollDirection } from '../types/arrowScroller.types';
import { UseArrowScrollerProps } from '../types/useArrowScroller.types';

const useArrowScroller = ({ containerRef, scrollAmount }: UseArrowScrollerProps) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    checkScroll();
    container.addEventListener('scroll', checkScroll);

    const debouncedCheckScroll = debounce(checkScroll, 200);

    const resizeObserver = new ResizeObserver(debouncedCheckScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      resizeObserver.disconnect();
    };
  }, [checkScroll]);

  const scroll = (direction: ScrollDirection) => {
    if (!containerRef.current) {
      return;
    }

    containerRef.current.scrollBy({
      left: direction === ScrollDirection.LEFT ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return { canScrollLeft, canScrollRight, scroll };
};

export default useArrowScroller;
